import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SectionHelp from '@/components/help/SectionHelp';
import VerticalExampleBox from '@/components/help/VerticalExampleBox';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import FieldHelp from '@/components/help/FieldHelp';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import helpContent from '@/help/helpContent';
import sensitiveActions from '@/help/sensitiveActions';
import {
  Megaphone, Plus, Eye, CheckCircle, XCircle, Archive, Send, ChevronRight,
  FileText, Clock, Shield, X as XIcon, Copy, Calendar, Truck, Users, Link2, Unlink
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600', ready_for_review: 'bg-amber-50 text-amber-700',
  approved: 'bg-emerald-50 text-emerald-700', rejected: 'bg-red-50 text-red-700',
  archived: 'bg-gray-100 text-gray-400',
};
const CHANNELS = [
  { value: 'whatsapp_future', label: 'WhatsApp (futuro)' },
  { value: 'email_future', label: 'Email (futuro)' },
  { value: 'sms_future', label: 'SMS (futuro)' },
  { value: 'social_future', label: 'Social (futuro)' },
  { value: 'generic', label: 'Genérico' },
];

export default function CampaignBuilder() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState({ key: '', name: '', objective: '', campaign_type: 'promotional', planned_channels: [], tone: '', risk_level: 'low' });
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [contents, setContents] = useState([]);
  const [preview, setPreview] = useState(null);
  const [contentForm, setContentForm] = useState({ channel: 'generic', content_type: 'message', subject: '', body: '', cta_text: '', variables: [], variable_examples: {} });
  const [schedules, setSchedules] = useState([]);
  const [deliveryRuns, setDeliveryRuns] = useState([]);
  const [linkedAudiences, setLinkedAudiences] = useState([]);
  const [allAudiences, setAllAudiences] = useState([]);
  const [linkAudienceId, setLinkAudienceId] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/tenant/campaigns?page_size=50');
      setCampaigns(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadDetail = async (id) => {
    const [c, ct] = await Promise.all([
      api.get(`/tenant/campaigns/${id}`), api.get(`/tenant/campaigns/${id}/contents`),
    ]);
    setDetail(c.data); setContents(ct.data || []); setTab('detail');
    try { const s = await api.get(`/tenant/campaigns/${id}/schedules`); setSchedules(s.data?.items || []); } catch { setSchedules([]); }
    try { const d = await api.get('/tenant/campaign-delivery/runs'); setDeliveryRuns((d.data?.items || []).filter(r => r.campaign_id === id)); } catch { setDeliveryRuns([]); }
    try { const la = await api.get(`/tenant/campaigns/${id}/audiences`); setLinkedAudiences(la.data || []); } catch { setLinkedAudiences([]); }
    try { const aa = await api.get('/tenant/audiences'); setAllAudiences(aa.data || []); } catch { setAllAudiences([]); }
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await api.put(`/tenant/campaigns/${editId}`, form);
        toast.success('Campaña actualizada');
      } else {
        await api.post('/tenant/campaigns', form);
        toast.success('Campaña creada');
      }
      setTab('list'); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAction = async (id, action) => {
    try {
      await api.post(`/tenant/campaigns/${id}/${action}`);
      toast.success(`Acción: ${action}`); loadDetail(id); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleArchive = async (id) => {
    try { await api.patch(`/tenant/campaigns/${id}/archive`); toast.success('Archivada'); load(); setTab('list'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handlePreview = async (id) => {
    try {
      const { data } = await api.post(`/tenant/campaigns/${id}/preview`);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleAddContent = async (id) => {
    try {
      await api.post(`/tenant/campaigns/${id}/contents`, contentForm);
      toast.success('Contenido añadido');
      setContentForm({ channel: 'generic', content_type: 'message', subject: '', body: '', cta_text: '', variables: [], variable_examples: {} });
      loadDetail(id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleVersion = async (id) => {
    try { await api.post(`/tenant/campaigns/${id}/versions`); toast.success('Versión creada'); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleLinkAudience = async (campaignId) => {
    if (!linkAudienceId) return;
    try {
      await api.post(`/tenant/campaigns/${campaignId}/audiences/${linkAudienceId}/link`);
      toast.success('Audiencia vinculada');
      setLinkAudienceId('');
      loadDetail(campaignId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleUnlinkAudience = async (campaignId, audienceId) => {
    try {
      await api.delete(`/tenant/campaigns/${campaignId}/audiences/${audienceId}/unlink`);
      toast.success('Audiencia desvinculada');
      loadDetail(campaignId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="campaign-builder-page">
      <SectionHelp module="campaigns" title={helpContent.campaigns?.title || 'Campaign Builder'} description={helpContent.campaigns?.description} whatItDoesNot={helpContent.campaigns?.whatItDoesNot} />
      <VerticalExampleBox module="campaigns" />

      {tab === 'list' && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#64748B]">{campaigns.length} campaña{campaigns.length !== 1 ? 's' : ''}</p>
            <Button size="sm" onClick={() => { setForm({ key: '', name: '', objective: '', campaign_type: 'promotional', planned_channels: [], tone: '', risk_level: 'low' }); setEditId(null); setTab('form'); }} data-testid="campaign-create-btn"><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
          </div>
          {campaigns.length === 0 ? (
            <EmptyStateGuide module="campaigns" message="Aún no tienes campañas. Crea tu primera campaña para diseñar contenido por canal." ctaLabel="Crear campaña" onCta={() => setTab('form')} />
          ) : (
            <div className="space-y-2" data-testid="campaign-list">
              {campaigns.map(c => (
                <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:border-[#94A3B8] transition-colors" onClick={() => loadDetail(c.id)} data-testid={`campaign-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{c.name}</p>
                    <p className="text-xs text-[#94A3B8]">{c.key} · {(c.planned_channels || []).join(', ') || 'sin canales'}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100'}`}>{c.status}</span>
                  <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'form' && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="campaign-form">
          <h3 className="text-sm font-bold">{editId ? 'Editar' : 'Nueva'} campaña</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldHelp label={helpContent.campaigns.fields.key.label} help={helpContent.campaigns.fields.key.help} htmlFor="campaign-key">
              <Input value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} data-testid="campaign-key" id="campaign-key" disabled={!!editId} />
            </FieldHelp>
            <FieldHelp label={helpContent.campaigns.fields.name.label} help={helpContent.campaigns.fields.name.help} htmlFor="campaign-name">
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="campaign-name" id="campaign-name" />
            </FieldHelp>
            <div className="md:col-span-2">
              <FieldHelp label={helpContent.campaigns.fields.objective.label} help={helpContent.campaigns.fields.objective.help} htmlFor="campaign-objective">
                <Textarea value={form.objective} onChange={e => setForm({ ...form, objective: e.target.value })} data-testid="campaign-objective" id="campaign-objective" />
              </FieldHelp>
            </div>
            <FieldHelp label={helpContent.campaigns.fields.campaign_type.label} help={helpContent.campaigns.fields.campaign_type.help} htmlFor="campaign-type">
              <select className="w-full border rounded px-2 py-1.5 text-xs" value={form.campaign_type} onChange={e => setForm({ ...form, campaign_type: e.target.value })} data-testid="campaign-type" id="campaign-type">
                <option value="promotional">Promocional</option><option value="informational">Informativo</option><option value="reminder">Recordatorio</option><option value="follow_up">Seguimiento</option>
              </select>
            </FieldHelp>
            <FieldHelp label={helpContent.campaigns.fields.tone.label} help={helpContent.campaigns.fields.tone.help} htmlFor="campaign-tone">
              <Input value={form.tone} onChange={e => setForm({ ...form, tone: e.target.value })} data-testid="campaign-tone" id="campaign-tone" placeholder="amigable, formal..." />
            </FieldHelp>
            <FieldHelp label={helpContent.campaigns.fields.risk_level.label} help={helpContent.campaigns.fields.risk_level.help} htmlFor="campaign-risk">
              <select className="w-full border rounded px-2 py-1.5 text-xs" value={form.risk_level} onChange={e => setForm({ ...form, risk_level: e.target.value })} data-testid="campaign-risk" id="campaign-risk">
                <option value="low">Bajo</option><option value="medium">Medio</option><option value="high">Alto</option>
              </select>
            </FieldHelp>
            <div className="md:col-span-2">
              <FieldHelp label={helpContent.campaigns.fields.planned_channels.label} help={helpContent.campaigns.fields.planned_channels.help} htmlFor="campaign-channels">
                <div className="flex flex-wrap gap-2 mt-1">
                  {CHANNELS.map(ch => (
                    <label key={ch.value} className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={(form.planned_channels || []).includes(ch.value)}
                        onChange={e => { const chs = form.planned_channels || []; setForm({ ...form, planned_channels: e.target.checked ? [...chs, ch.value] : chs.filter(x => x !== ch.value) }); }} />
                      {ch.label}
                    </label>
                  ))}
                </div>
              </FieldHelp>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setTab('list')}>Cancelar</Button>
            <Button size="sm" onClick={handleSave} data-testid="campaign-save-btn">{editId ? 'Guardar' : 'Crear'}</Button>
          </div>
        </div>
      )}

      {tab === 'detail' && detail && (
        <div className="space-y-4" data-testid="campaign-detail">
          <div className="flex items-center gap-2">
            <button onClick={() => { setTab('list'); setDetail(null); setPreview(null); }} className="text-xs text-[#64748B] hover:text-[#0F172A]">← Volver</button>
            <h3 className="text-sm font-bold flex-1">{detail.name}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[detail.status]}`}>{detail.status}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Tipo:</span> {detail.campaign_type}</div>
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Canales:</span> {(detail.planned_channels || []).length}</div>
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Riesgo:</span> {detail.risk_level}</div>
            <div className="bg-white border rounded-lg p-2"><span className="text-[#94A3B8]">Aprobación:</span> {detail.approval_status}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            {detail.status === 'draft' && (
              <SensitiveActionHelp action={sensitiveActions.submit_review} onConfirm={() => handleAction(detail.id, 'submit-review')}>
                <Button size="sm" data-testid="campaign-submit-review-btn"><Send className="w-3.5 h-3.5 mr-1" /> Enviar a revisión</Button>
              </SensitiveActionHelp>
            )}
            {detail.status === 'ready_for_review' && <>
              <SensitiveActionHelp action={sensitiveActions.campaign_approve} onConfirm={() => handleAction(detail.id, 'approve')}>
                <Button size="sm" data-testid="campaign-approve-btn"><CheckCircle className="w-3.5 h-3.5 mr-1" /> Aprobar</Button>
              </SensitiveActionHelp>
              <SensitiveActionHelp action={sensitiveActions.campaign_reject} onConfirm={() => handleAction(detail.id, 'reject')}>
                <Button size="sm" variant="outline" className="text-red-600" data-testid="campaign-reject-btn"><XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar</Button>
              </SensitiveActionHelp>
            </>}
            <SensitiveActionHelp action={sensitiveActions.campaign_preview} onConfirm={() => handlePreview(detail.id)}>
              <Button size="sm" variant="outline" data-testid="campaign-preview-btn"><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
            </SensitiveActionHelp>
            <SensitiveActionHelp action={sensitiveActions.campaign_version} onConfirm={() => handleVersion(detail.id)}>
              <Button size="sm" variant="outline"><Copy className="w-3.5 h-3.5 mr-1" /> Versionar</Button>
            </SensitiveActionHelp>
            {detail.status !== 'archived' && (
              <SensitiveActionHelp action={sensitiveActions.archive} onConfirm={() => handleArchive(detail.id)}>
                <Button size="sm" variant="outline"><Archive className="w-3.5 h-3.5 mr-1" /> Archivar</Button>
              </SensitiveActionHelp>
            )}
            <Button size="sm" variant="outline" onClick={() => { setForm(detail); setEditId(detail.id); setTab('form'); }}><FileText className="w-3.5 h-3.5 mr-1" /> Editar</Button>
          </div>

          {preview && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-blue-700"><Eye className="w-3.5 h-3.5" /> Vista previa sin envío</div>
              {preview.warnings?.length > 0 && <div className="text-xs text-amber-600">{preview.warnings.join('; ')}</div>}
              {(preview.rendered || []).map((r, i) => (
                <div key={i} className="bg-white rounded-lg p-3 text-xs space-y-1">
                  <span className="text-[10px] bg-blue-100 px-1.5 py-0.5 rounded">{r.channel}</span>
                  {r.subject && <p className="font-medium">{r.subject}</p>}
                  <p className="whitespace-pre-wrap">{r.body}</p>
                  {r.cta_text && <p className="text-blue-600 font-medium">{r.cta_text}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Audiencias vinculadas (S15E) */}
          <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="campaign-audiences-section">
            <h4 className="text-xs font-bold flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Audiencias vinculadas</h4>
            {linkedAudiences.length === 0
              ? <p className="text-xs text-[#94A3B8]">No hay audiencias vinculadas. Vincula una audiencia para segmentar tu campaña.</p>
              : linkedAudiences.map(la => {
                  const aud = allAudiences.find(a => a.id === la.audience_id);
                  return (
                    <div key={la.id} className="flex items-center justify-between border rounded-lg px-3 py-2 text-xs" data-testid={`linked-audience-${la.audience_id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Users className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
                        <span className="font-medium text-[#0F172A] truncate">{aud?.name || la.audience_id.slice(0, 8)}</span>
                        {la.audience_snapshot?.total_members != null && <span className="text-[#94A3B8]">({la.audience_snapshot.total_members} miembros)</span>}
                      </div>
                      <SensitiveActionHelp action={sensitiveActions.unlink_audience} onConfirm={() => handleUnlinkAudience(detail.id, la.audience_id)}>
                        <button className="flex items-center gap-1 text-red-500 hover:text-red-700" data-testid={`unlink-audience-${la.audience_id}`}>
                          <Unlink className="w-3 h-3" /> Desvincular
                        </button>
                      </SensitiveActionHelp>
                    </div>
                  );
                })
            }
            <div className="flex gap-2 items-center border-t pt-2">
              <select value={linkAudienceId} onChange={e => setLinkAudienceId(e.target.value)}
                className="flex-1 border rounded px-2 py-1.5 text-xs" data-testid="link-audience-select">
                <option value="">Seleccionar audiencia...</option>
                {allAudiences.filter(a => a.status === 'active' && !linkedAudiences.some(la => la.audience_id === a.id)).map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.estimated_count} miembros)</option>
                ))}
              </select>
              <SensitiveActionHelp action={sensitiveActions.link_audience} onConfirm={() => handleLinkAudience(detail.id)} disabled={!linkAudienceId}>
                <Button size="sm" disabled={!linkAudienceId} data-testid="link-audience-btn"><Link2 className="w-3.5 h-3.5 mr-1" /> Vincular</Button>
              </SensitiveActionHelp>
            </div>
          </div>

          {/* Programación y Preflight (S15C) */}
          <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="campaign-schedules-section">
            <h4 className="text-xs font-bold flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Programación y Preflight</h4>
            {schedules.length === 0
              ? <p className="text-xs text-[#94A3B8]">No hay programaciones para esta campaña.</p>
              : schedules.map(s => (
                <div key={s.id} className="flex items-center justify-between border rounded-lg px-3 py-1.5 text-xs">
                  <div>
                    <span className="font-medium text-[#0F172A]">{s.planned_channel}</span>
                    <span className="ml-2 text-[#94A3B8]">{s.status}</span>
                    {s.preflight_status !== 'not_run' && <span className={`ml-2 ${s.preflight_status === 'passed' ? 'text-emerald-600' : s.preflight_status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>Preflight: {s.preflight_status}</span>}
                  </div>
                  <span className="text-[#94A3B8]">{s.planned_start_at ? new Date(s.planned_start_at).toLocaleDateString('es-MX') : '—'}</span>
                </div>
              ))
            }
            <Button size="sm" variant="outline" onClick={() => navigate('/tenant/marketing/schedules')} data-testid="campaign-go-schedules-btn">
              <Calendar className="w-3.5 h-3.5 mr-1" /> Ir a Programación
            </Button>
          </div>

          {/* Entregas (S15D) */}
          <div className="bg-white border rounded-xl p-4 space-y-2" data-testid="campaign-delivery-section">
            <h4 className="text-xs font-bold flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Entregas</h4>
            {deliveryRuns.length === 0
              ? <p className="text-xs text-[#94A3B8]">No hay entregas para esta campaña.</p>
              : deliveryRuns.slice(0, 5).map(r => (
                <div key={r.id} className="flex items-center justify-between border rounded-lg px-3 py-1.5 text-xs">
                  <div>
                    <span className="font-medium text-[#0F172A]">{r.name}</span>
                    <span className="ml-2 text-[#94A3B8]">{r.status} | {r.delivery_mode}</span>
                  </div>
                  <span className="text-[#94A3B8]">{r.total_recipients} recipients</span>
                </div>
              ))
            }
            <Button size="sm" variant="outline" onClick={() => navigate('/tenant/marketing/delivery')} data-testid="campaign-go-delivery-btn">
              <Truck className="w-3.5 h-3.5 mr-1" /> Ir a Entregas
            </Button>
          </div>

          <div className="bg-white border rounded-xl p-4 space-y-3">
            <h4 className="text-xs font-bold">Contenidos ({contents.length})</h4>
            {contents.map(ct => (
              <div key={ct.id} className="border rounded-lg p-2 text-xs space-y-1">
                <div className="flex gap-2"><span className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{ct.channel}</span><span className="text-[#94A3B8]">{ct.content_type}</span></div>
                {ct.subject && <p className="font-medium">{ct.subject}</p>}
                <p className="text-[#475569]">{ct.body?.substring(0, 200)}</p>
              </div>
            ))}
            <div className="border-t pt-3 space-y-2">
              <h5 className="text-xs font-medium text-[#64748B]">Añadir contenido</h5>
              <div className="grid grid-cols-2 gap-2">
                <select className="border rounded px-2 py-1 text-xs" value={contentForm.channel} onChange={e => setContentForm({ ...contentForm, channel: e.target.value })}>
                  {CHANNELS.map(ch => <option key={ch.value} value={ch.value}>{ch.label}</option>)}
                </select>
                <select className="border rounded px-2 py-1 text-xs" value={contentForm.content_type} onChange={e => setContentForm({ ...contentForm, content_type: e.target.value })}>
                  <option value="message">Mensaje</option><option value="subject">Asunto</option><option value="body">Cuerpo</option><option value="cta">CTA</option>
                </select>
              </div>
              <Input placeholder="Asunto" value={contentForm.subject} onChange={e => setContentForm({ ...contentForm, subject: e.target.value })} className="text-xs" />
              <Textarea placeholder="Cuerpo del mensaje. Usa {{nombre}}, {{fecha}} para variables." value={contentForm.body} onChange={e => setContentForm({ ...contentForm, body: e.target.value })} className="text-xs h-20" />
              <Input placeholder="Texto CTA" value={contentForm.cta_text} onChange={e => setContentForm({ ...contentForm, cta_text: e.target.value })} className="text-xs" />
              <Button size="sm" onClick={() => handleAddContent(detail.id)} data-testid="campaign-add-content-btn">Añadir contenido</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
